// The Chilean RUT validators, on their own: pure functions with no config in
// them, so the WEB bundle can check a RUT's check digit with the very code the
// API runs (`clConfig.companyId.validate`), and the two cannot drift. The
// server's config (`cl.ts`) re-exports every name below unchanged.
//
// ── Chilean RUT (Rol Único Tributario) ────────────────────────────────────────
//
// A body of up to eight digits and a check digit (`0`–`9` or `K`), printed as
// `76.086.428-5`. The CANONICAL form — what the forms send, what is stored in
// `organizations.tin` / `tender_results.winner_tin` and what a company URL
// starts with — has no dots, a dash and an upper-case `K`: `76086428-5`,
// `61704000-K` (Liciora AGENT-RULES §6). `validate` takes the canonical form
// only; {@link canonicalRut} turns anything a person types into it.
//
// The check digit is the SII's módulo 11: the body's digits, right to left, are
// multiplied by 2, 3, 4, 5, 6, 7, 2, 3, … and summed; the digit is
// `11 − (sum mod 11)`, where 11 is written `0` and 10 is written `K`. Checked in
// `cl.test.ts` against the published RUTs of Codelco (61.704.000-K), Banco de
// Chile (97.004.000-5), BancoEstado (97.030.000-7) and the SII itself
// (60.803.000-K).

/** The canonical shape: 1–8 digits (no leading zero), a dash, the check digit. */
const CANONICAL = /^([1-9]\d{0,7})-([0-9K])$/;

/** The módulo-11 check digit of a RUT body (digits only), `'0'`–`'9'` or `'K'`. */
export function rutCheckDigit(body: string): string {
  let sum = 0;
  let factor = 2;
  for (let i = body.length - 1; i >= 0; i--) {
    sum += Number(body[i]) * factor;
    factor = factor === 7 ? 2 : factor + 1;
  }
  const r = 11 - (sum % 11);
  return r === 11 ? '0' : r === 10 ? 'K' : String(r);
}

/**
 * Any way a RUT is written («76.086.428-5», «76086428-5», «760864285»,
 * «61.704.000-k», « 61704000 K ») → the canonical `digits-DV`, or `null` when
 * what remains is not a body and a check digit. Shape only — the check digit is
 * NOT verified here ({@link isValidRut} does that), so a form can show a typo'd
 * RUT back to the reader in the canonical spelling.
 */
export function canonicalRut(raw: string): string | null {
  const s = raw.toUpperCase().replace(/[\s.\-\u2010-\u2015]/g, '');
  const m = /^0*([1-9]\d{0,7})([0-9K])$/.exec(s);
  return m ? `${m[1]}-${m[2]}` : null;
}

/** A canonical RUT (`76086428-5`) whose check digit is right. */
export function isValidRut(id: string): boolean {
  const m = CANONICAL.exec(id);
  return m !== null && rutCheckDigit(m[1]) === m[2];
}

/**
 * The body from which a RUT is a LEGAL ENTITY by the SII's numbering: natural
 * persons are numbered in order of registration and sit far below it (the
 * highest are in the 20–30 millions today), while companies and other legal
 * persons receive bodies from 50 000 000 up (`76…`, `77…` for new companies,
 * `6x…` for public bodies, `9x…` for the older companies). A heuristic, not a
 * register lookup (Liciora AGENT-RULES §6): its only job is to keep every
 * natural person's page out of the index.
 */
export const CL_LEGAL_ENTITY_MIN_BODY = 50_000_000;

/** A valid RUT whose body is a legal entity's ({@link CL_LEGAL_ENTITY_MIN_BODY}). */
export function isLegalEntityRut(id: string): boolean {
  if (!isValidRut(id)) return false;
  return Number(id.slice(0, id.indexOf('-'))) >= CL_LEGAL_ENTITY_MIN_BODY;
}
